// Shared pinger reading helpers — the single source of truth for parsing ping metrics and classifying
// them red/yellow/green, used by every pinger surface (list-row signal, detail charts, dashboard widget).
//
// Readings come from continuum.metrics rows (category='ping'), shape { readings: [{key, scope, value}] } —
// the same row the collector's `a8 worker net-ping` -> push-metrics writes. The thresholds mirror the
// pinger.cp convention bounds EXACTLY (latency 150/500, loss 10/50, cert.daysRemaining 21/7 INVERTED) so
// the UI colors match the breaker the checker actually wrote. A monitor's extra_data may override these.

export type PingLevel = "green" | "yellow" | "red" | "unknown";

export interface Reading {
  key: string;
  scope?: string;
  value: number;
  unit?: string; // the value's unit ("ms","percent","bool",...); makes the value self-describing
}

// The raw `data.readings` jsonb (or its already-parsed form) from a metrics row.
export function parseReadings(raw: unknown): Reading[] {
  const arr = Array.isArray(raw)
    ? raw
    : raw && typeof raw === "object" && Array.isArray((raw as { readings?: unknown }).readings)
      ? (raw as { readings: unknown[] }).readings
      : [];
  return (arr as unknown[])
    .map((r) => r as Record<string, unknown>)
    .filter((r) => typeof r?.key === "string" && r.value != null && !Number.isNaN(Number(r.value)))
    .map((r) => ({
      key: String(r.key),
      scope: r.scope ? String(r.scope) : undefined,
      value: Number(r.value),
      unit: r.unit ? String(r.unit) : undefined,
    }));
}

// formatUnit renders a unit as a human suffix appended to a value. "percent" -> "%", "ms"/"days"
// stay as-is (with a space), "bool"/"count"/"" render nothing (a 0/1 or bare count needs no unit).
export function formatUnit(unit: string | undefined): string {
  switch (unit) {
    case "percent":
      return "%";
    case "ms":
      return " ms";
    case "days":
      return " days";
    case "seconds":
      return " s";
    case "bytes":
      return " B";
    default:
      return ""; // bool / count / unknown -> no suffix
  }
}

// formatValue renders a reading value with its unit, e.g. 22 + "ms" -> "22 ms", 95 + "percent" -> "95%".
export function formatValue(value: number, unit: string | undefined): string {
  return `${value}${formatUnit(unit)}`;
}

// firstReading returns the value of the first reading whose key matches, or undefined.
export function firstReading(readings: Reading[], key: string): number | undefined {
  return readings.find((r) => r.key === key)?.value;
}

// PingThresholds are the yellow/red bounds for the three alerted reading keys. yellow/red are the
// crossing points; cert is INVERTED (fewer days remaining is worse). These default to the pinger.cp
// convention; a monitor's extra_data thresholds override them.
export interface PingThresholds {
  latencyYellowMs: number;
  latencyRedMs: number;
  lossYellowPct: number;
  lossRedPct: number;
  certWarnDays: number;
  certRedDays: number;
}

export const DEFAULT_PING_THRESHOLDS: PingThresholds = {
  latencyYellowMs: 150,
  latencyRedMs: 500,
  lossYellowPct: 10,
  lossRedPct: 50,
  certWarnDays: 21,
  certRedDays: 7,
};

// thresholdsFromExtraData reads the pinger config a provisioner stored on monitor.extra_data, falling
// back to the convention default for any missing field. extra_data may be a parsed object or a JSON string.
export function thresholdsFromExtraData(extraData: unknown): PingThresholds {
  let obj: Record<string, unknown> = {};
  if (typeof extraData === "string") {
    try {
      obj = JSON.parse(extraData) as Record<string, unknown>;
    } catch {
      obj = {};
    }
  } else if (extraData && typeof extraData === "object") {
    obj = extraData as Record<string, unknown>;
  }
  const num = (k: keyof PingThresholds): number =>
    typeof obj[k] === "number" ? (obj[k] as number) : DEFAULT_PING_THRESHOLDS[k];
  return {
    latencyYellowMs: num("latencyYellowMs"),
    latencyRedMs: num("latencyRedMs"),
    lossYellowPct: num("lossYellowPct"),
    lossRedPct: num("lossRedPct"),
    certWarnDays: num("certWarnDays"),
    certRedDays: num("certRedDays"),
  };
}

// levelForAscending classifies a reading where HIGHER is worse (latency, loss): >=red -> red,
// >=yellow -> yellow, else green.
function levelForAscending(value: number, yellow: number, red: number): PingLevel {
  if (value >= red) return "red";
  if (value >= yellow) return "yellow";
  return "green";
}

// levelForDescending classifies a reading where LOWER is worse (cert.daysRemaining): <=red -> red,
// <=yellow -> yellow, else green.
function levelForDescending(value: number, yellow: number, red: number): PingLevel {
  if (value <= red) return "red";
  if (value <= yellow) return "yellow";
  return "green";
}

// levelForReading classifies one reading key against the thresholds. `reachable` is special: 0 -> red
// (the target did not answer at all), 1 -> green. Unknown keys return "unknown".
export function levelForReading(key: string, value: number, t: PingThresholds): PingLevel {
  switch (key) {
    case "latency":
      return levelForAscending(value, t.latencyYellowMs, t.latencyRedMs);
    case "loss":
      return levelForAscending(value, t.lossYellowPct, t.lossRedPct);
    case "cert.daysRemaining":
      return levelForDescending(value, t.certWarnDays, t.certRedDays);
    case "reachable":
      return value <= 0 ? "red" : "green";
    default:
      return "unknown";
  }
}

// muiColor maps a level to an MUI Chip/semantic color. Shared by every surface so the palette is
// identical to the decision table (which uses the same mapping for check_results levels).
export function muiColor(level: PingLevel | string | undefined): "error" | "warning" | "success" | "default" {
  if (level === "red") return "error";
  if (level === "yellow") return "warning";
  if (level === "green") return "success";
  return "default";
}

// PingSignal is the compact, render-ready summary of a pinger's latest probe: the three alerted
// readings with their classified levels, plus reachability. Any field is undefined when its reading
// is absent (e.g. cert only exists for https probes).
//
// A pinger may probe MANY targets (one monitor, N hosts — a fleet liveness check). Every reading
// carries its target in `scope`, so this summary is the WORST across all of them, matching what
// pinger.cp does to drive the breaker. `targets` carries the per-host breakdown so a surface can
// name WHICH host is bad; `worstTarget` is that host.
export interface PingSignal {
  latencyMs?: number;
  latencyLevel: PingLevel;
  lossPct?: number;
  lossLevel: PingLevel;
  certDays?: number;
  certLevel: PingLevel;
  reachable?: boolean;
  // Multi-target: the per-scope breakdown, and the worst scope. Empty/undefined for a single
  // unscoped probe, so single-target surfaces are unaffected.
  targets?: PingTargetSignal[];
  worstTarget?: string;
}

// PingTargetSignal is one target's slice of a multi-target probe.
export interface PingTargetSignal extends Omit<PingSignal, "targets" | "worstTarget"> {
  target: string;
  level: PingLevel; // the worst of this target's own levels
}

const LEVEL_RANK: Record<string, number> = { unknown: 0, green: 1, yellow: 2, red: 3 };

function worstLevel(...levels: (PingLevel | undefined)[]): PingLevel {
  let worst: PingLevel = "unknown";
  for (const l of levels) {
    if (l && (LEVEL_RANK[l] ?? 0) > (LEVEL_RANK[worst] ?? 0)) worst = l;
  }
  return worst;
}

// signalFor collapses ONE target's readings (or an unscoped set) into a PingSignal.
function signalFor(readings: Reading[], t: PingThresholds): Omit<PingSignal, "targets" | "worstTarget"> {
  const lossPct = firstReading(readings, "loss");
  const certDays = firstReading(readings, "cert.daysRemaining");
  const reach = firstReading(readings, "reachable");
  // Latency is only meaningful when the target ANSWERED. On an unreachable target (reachable=0) or a
  // total loss (100%), there was no round-trip, so the collector's latency (0 or absent) is noise — show
  // NO latency rather than a misleading "0.0ms". Suppress it to undefined so the chip isn't rendered.
  const answered = reach == null ? true : reach > 0;
  const noRoundTrip = !answered || lossPct === 100;
  const latencyMs = noRoundTrip ? undefined : firstReading(readings, "latency");
  return {
    latencyMs,
    latencyLevel: latencyMs == null ? "unknown" : levelForReading("latency", latencyMs, t),
    lossPct,
    lossLevel: lossPct == null ? "unknown" : levelForReading("loss", lossPct, t),
    certDays,
    certLevel: certDays == null ? "unknown" : levelForReading("cert.daysRemaining", certDays, t),
    reachable: reach == null ? undefined : reach > 0,
  };
}

// toPingSignal collapses a readings array + thresholds into the render-ready PingSignal.
//
// With several targets it reports the WORST of them — the same rule pinger.cp uses to drive the
// breaker, so the UI and the breaker can never disagree. This matters: reading the first match
// regardless of scope (which is what this did before multi-target pingers existed) would show
// host #1's numbers and render a red host #3 invisible while the breaker went red anyway.
export function toPingSignal(readings: Reading[], t: PingThresholds = DEFAULT_PING_THRESHOLDS): PingSignal {
  const scopes = Array.from(
    new Set(readings.map((r) => r.scope).filter((s): s is string => !!s)),
  );

  // No scopes: a single unscoped probe (or legacy readings). Unchanged behaviour.
  if (scopes.length === 0) {
    return signalFor(readings, t);
  }

  const targets: PingTargetSignal[] = scopes.map((scope) => {
    const sig = signalFor(readings.filter((r) => r.scope === scope), t);
    return {
      ...sig,
      target: scope,
      level: worstLevel(sig.latencyLevel, sig.lossLevel, sig.certLevel,
        // an unreachable target is red regardless of what else is present
        sig.reachable === false ? "red" : undefined),
    };
  });

  // Sort worst-first so any surface that truncates shows the problems, not the healthy hosts.
  const ranked = [...targets].sort((a, b) => (LEVEL_RANK[b.level] ?? 0) - (LEVEL_RANK[a.level] ?? 0));
  const worst = ranked[0];

  return {
    ...worst,
    targets: ranked,
    worstTarget: worst.target,
  };
}
